import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ObservationsProps } from './Observations.model'
import styles from './Observations.module.scss'
import { EditorEvents, EditorTool, SimpleEditor, useElfeEditor, styleTables } from '@follow/elfe'
import { debounce } from 'lodash'

const editorTools: Array<EditorTool> = [
  'caseSwap',
  'heading',
  'fontFamily',
  'fontSize',
  'marks',
  'colors',
  'align',
  'list',
  'lineHeight',
  'unformat',
  'undo',
  'table',
]

export const Observations: FunctionComponent<ObservationsProps> = ({
  content,
  onChange,
  readonly,
}) => {
  const handleDebouncedContent = useMemo(
    () =>
      debounce((event: EditorEvents['update']) => {
        onChange(styleTables(event.editor.getHTML()))
      }, 500),
    [onChange],
  )

  const handleContentChange = useCallback(
    (event: EditorEvents['update']) => {
      handleDebouncedContent(event)
    },
    [handleDebouncedContent],
  )

  const { editor, config } = useElfeEditor(
    {
      editorType: 'simple',
      tools: editorTools,
      themes: ['rounded', 'no-paragraph-space'],
    },
    {
      content,
      onUpdate: handleContentChange,
    },
  )

  const observationContainerRef = useRef(null)

  const [toolbarTop, setToolbarTop] = useState<number>(
    // @ts-ignore
    Math.max(observationContainerRef?.current?.getBoundingClientRect().top + 85, 160),
  )

  const [toolbarWidth, setToolbarWidth] = useState<number>(
    // @ts-ignore
    observationContainerRef?.current?.getBoundingClientRect().width - 40,
  )

  useEffect(() => {
    if (!observationContainerRef.current) return
    const updatePosition = () => {
      if (observationContainerRef.current) {
        // @ts-ignore
        setToolbarTop(Math.max(observationContainerRef.current.getBoundingClientRect().top, 150))
        // @ts-ignore
        setToolbarWidth(observationContainerRef.current.getBoundingClientRect().width - 40)
      }
    }

    const delayedUpdatePosition = () => {
      setTimeout(updatePosition, 300)
    }
    // Get initial position
    // @ts-ignore
    // setToolbarWidth(observationContainerRef?.current?.getBoundingClientRect().width - 40)
    // // @ts-ignore
    // setToolbarTop(Math.max(observationContainerRef.current.getBoundingClientRect().top + 85, 150))

    // Update on resize or scroll if needed
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)
    document.addEventListener('layout.resize', delayedUpdatePosition)
    document.dispatchEvent(new Event('layout.resize'))
    return () => {
      document.removeEventListener('layout.resize', delayedUpdatePosition)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
    }
  }, [])

  useEffect(() => {
    if (editor && !editor.isFocused) {
      editor?.commands.setContent(styleTables(content))
    }
  }, [content, editor])

  return (
    <div
      className={readonly ? styles.observationsreadonly : styles.observations}
      data-test-id="editor-observations"
      data-dictation-label="Observations"
      ref={observationContainerRef}
    >
      <SimpleEditor
        editor={editor}
        config={config}
        disabled={readonly}
        toolbarTop={toolbarTop}
        toolbarStyle={{ width: toolbarWidth }}
      />
    </div>
  )
}
